import { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";

import { getIsoLocaleFromCountry, IsoLocale, Locales } from "@simplyk/common";
import type { i18n as I18nInstance } from "i18next";
// eslint-disable-next-line no-restricted-imports
import { i18n as dashboardI18n } from "next-i18next";
// eslint-disable-next-line no-restricted-imports
import { I18nextProvider } from "react-i18next";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { LocaleContext, useLocaleContext } from "@/common/contexts/LocaleContext";

const PREVIEW_NAMESPACES = ["common", "donationForm", "ticketing"];

const getBaseLanguage = (isoLocale: string): string => isoLocale.split("-")[0];

export const loadPreviewNamespaces = async (
  instance: I18nInstance,
  languages: string[],
  namespaces = PREVIEW_NAMESPACES
): Promise<void> => {
  await Promise.all(
    languages.flatMap((language) =>
      namespaces.map(async (namespace) => {
        if (instance.hasResourceBundle(language, namespace)) {
          return;
        }
        const response = await fetch(`/locales/${language}/${namespace}.json`);
        if (!response.ok) {
          return;
        }
        instance.addResourceBundle(language, namespace, await response.json(), true, true);
      })
    )
  );
};

interface PreviewLocaleProviderProps {
  locale: Locales;
}

export const PreviewLocaleProvider: FC<PropsWithChildren<PreviewLocaleProviderProps>> = ({ locale, children }) => {
  const { organization } = useCurrentUserContext();
  const { isoLocale: dashboardIsoLocale, setIsoLocale } = useLocaleContext();

  const previewIsoLocale = (getIsoLocaleFromCountry(organization?.country, locale) ?? dashboardIsoLocale) as IsoLocale;
  const [previewI18n, setPreviewI18n] = useState<I18nInstance | null>(null);

  useEffect(() => {
    if (!dashboardI18n || previewIsoLocale === dashboardIsoLocale) {
      setPreviewI18n(null);
      return;
    }

    let isStale = false;
    const instance = dashboardI18n.cloneInstance({
      lng: previewIsoLocale,
      fallbackLng: [
        ...new Set([
          getBaseLanguage(previewIsoLocale),
          dashboardIsoLocale,
          getBaseLanguage(dashboardIsoLocale),
          Locales.EN,
        ]),
      ],
    });

    void loadPreviewNamespaces(instance, [previewIsoLocale, getBaseLanguage(previewIsoLocale)]).then(() => {
      if (!isStale) {
        setPreviewI18n(instance);
      }
    });

    return () => {
      isStale = true;
    };
  }, [dashboardIsoLocale, previewIsoLocale]);

  const localeContextValue = useMemo(
    () => ({ isoLocale: previewIsoLocale, locale, setIsoLocale }),
    [locale, previewIsoLocale, setIsoLocale]
  );

  return (
    <LocaleContext.Provider value={localeContextValue}>
      {previewI18n ? <I18nextProvider i18n={previewI18n}>{children}</I18nextProvider> : children}
    </LocaleContext.Provider>
  );
};
